// Message-row grouping rules.
//
// Slack collapses consecutive same-sender messages within a short
// time window into a "head + continuation" block: only the first row
// shows the avatar, name, and timestamp; subsequent rows render with
// just the body, aligned with the head's body.
//
// Pass 1.5: a message is a continuation when it satisfies all three:
//   (a) same sender as the immediately previous message in display
//       order
//   (b) within `gapSeconds` of the previous message's ts (default 300
//       seconds = 5 minutes)
//   (c) not preceded by a date separator — date separators always
//       force a fresh head row
//
// The decision is purely local: callers walk the rendered sequence
// and reset state on date-separator boundaries (and on reply blocks
// in the thread drawer; the parent message is always a head).

export const GROUPING_GAP_SECONDS = 300;

export interface GroupingPrev {
  user: string | undefined;
  ts: string;
}

export function isGroupedContinuation(
  prev: GroupingPrev | null,
  curr: GroupingPrev,
  gapSeconds: number = GROUPING_GAP_SECONDS,
): boolean {
  if (!prev) return false;
  // Missing user_id breaks grouping — treat as "no previous sender".
  if (!prev.user || !curr.user) return false;
  if (prev.user !== curr.user) return false;
  const dt = parseFloat(curr.ts) - parseFloat(prev.ts);
  // Defensive: NaN or negative gap (malformed ts) → not a continuation.
  if (!Number.isFinite(dt) || dt < 0) return false;
  return dt < gapSeconds;
}
